import { useEffect, useRef } from "react";
import OneSignal from "react-onesignal";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;
let initialized = false;

/**
 * Mount once at app root. Initialises the OneSignal Web SDK and, whenever the
 * auth user changes, calls login(external_id) so pushes can target by our user.id.
 */
export default function PushInit() {
  const { user } = useAuth();
  const promptedRef = useRef(false);

  // One-time SDK init
  useEffect(() => {
    if (initialized || !APP_ID || typeof window === "undefined") return;
    initialized = true;
    OneSignal.init({
      appId: APP_ID,
      allowLocalhostAsSecureOrigin: true,
      promptOptions: {
        slidedown: {
          prompts: [
            {
              type: "push",
              autoPrompt: false,
              text: {
                actionMessage:
                  "Get notified when you receive gifts, comments, follows, and live streams from your favourite creators.",
                acceptButton: "Allow",
                cancelButton: "Not now",
              },
            },
          ],
        },
      },
      notifyButton: { enable: false },
      serviceWorkerParam: { scope: "/" },
      serviceWorkerPath: "OneSignalSDKWorker.js",
    }).catch((e) => console.warn("OneSignal init failed", e));
  }, []);

  // Sync external_id and ask for permission once per session
  useEffect(() => {
    (async () => {
      try {
        if (user) {
          // After a fresh login, gently ask for permission once.
          // Persist the flag BEFORE the login attempt so we don't re-prompt
          // on every page load when OneSignal init is misconfigured.
          if (!promptedRef.current) {
            promptedRef.current = true;
            const alreadyAsked = window.localStorage.getItem("gw_push_prompted");
            if (!alreadyAsked) {
              window.localStorage.setItem("gw_push_prompted", "1");
              setTimeout(async () => {
                try {
                  if (OneSignal.Slidedown?.promptPush) {
                    await OneSignal.Slidedown.promptPush();
                  } else if (OneSignal.Notifications?.requestPermission) {
                    await OneSignal.Notifications.requestPermission();
                  }
                } catch {
                  /* user may decline; gating already persisted */
                }
              }, 4000);
            }
          }
          if (typeof OneSignal.login === "function") {
            await OneSignal.login(String(user.id));
          }
        } else if (initialized && typeof OneSignal.logout === "function") {
          await OneSignal.logout();
        }
      } catch (e) {
        /* never block the app on push errors */
      }
    })();
  }, [user]);

  return null;
}

/** Programmatic helper to trigger the permission prompt from a button click. */
export async function requestPushPermission() {
  try {
    if (OneSignal.Slidedown?.promptPush) {
      await OneSignal.Slidedown.promptPush({ force: true });
    } else if (OneSignal.Notifications?.requestPermission) {
      await OneSignal.Notifications.requestPermission();
    }
    return true;
  } catch (e) {
    return false;
  }
}

/** Send a server-side test push to the current user. */
export async function sendTestPush() {
  try {
    const { data } = await api.post("/push/test");
    return data;
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || e.message };
  }
}
